/*
 * Схемы адресов браузера 006 (contracts/http-api.md): компьютеры, агенты, разговоры, лента, сообщения, запросы
 * агента, WebSocket браузера. Их берут сервер (`api/src/modules/{connector,agent-thread}`) и интерфейс
 * (`web/src/lib/agent-api.ts`).
 */
import { z } from 'zod';
import { TimelineRow } from '../agent-events/timeline';
import { AgentHealth, AgentIdSchema, ModelInfo } from '../connector/protocol';

export const EnrollmentIssued = z.strictObject({
  command: z.string(),
  commandMasked: z.string(),
  expiresAt: z.string(),
});
export type EnrollmentIssued = z.infer<typeof EnrollmentIssued>;

export const ConnectorView = z.strictObject({
  id: z.number().int(),
  name: z.string(),
  os: z.string(),
  phase: z.enum(['pending', 'active', 'disabled']),
  online: z.boolean(),
  lastSeenAt: z.string().nullable(),
  disabledAt: z.string().nullable(),
  version: z.string().nullable(),
  agents: z.array(AgentHealth),
  /** Установки агентов: идущая и последняя законченная (ход — последние 200 строк). */
  installs: z.array(
    z.strictObject({
      agent: AgentIdSchema,
      action: z.enum(['install', 'update']),
      status: z.enum(['running', 'ok', 'failed']),
      startedAt: z.string(),
      finishedAt: z.string().nullable(),
      tail: z.array(z.string()),
    }),
  ),
  threads: z.number().int(),
  /** Только в списке администратора системы. */
  owner: z.string().optional(),
});
export type ConnectorView = z.infer<typeof ConnectorView>;
export const ConnectorList = z.strictObject({ connectors: z.array(ConnectorView) });

export const ModelsResponse = z.strictObject({
  models: z.array(ModelInfo),
  /** Каталог старше 10 минут: компьютер не ответил. */
  stale: z.boolean(),
  /** Показан запасной список (contracts/ui.md §3, «модели не загрузились»). */
  fallback: z.boolean(),
});
export type ModelsResponse = z.infer<typeof ModelsResponse>;

export const THREAD_STATUSES = ['idle', 'running', 'waiting', 'stopping', 'error'] as const;
export const ThreadStatus = z.enum(THREAD_STATUSES);
export type ThreadStatus = z.infer<typeof ThreadStatus>;
export const ThreadPage = z.enum(['rules', 'day', 'assistant']);

export const ThreadView = z.strictObject({
  id: z.string(),
  title: z.string(),
  agent: AgentIdSchema,
  model: z.string().nullable(),
  effort: z.string().nullable(),
  status: ThreadStatus,
  archived: z.boolean(),
  page: ThreadPage,
  connectorId: z.number().int(),
  connectorName: z.string(),
  readOnly: z.boolean(),
  allowDraft: z.boolean(),
  memPercent: z.number().int().nullable(),
  seq: z.number().int(),
  createdAt: z.string(),
  lastTurnAt: z.string().nullable(),
});
export type ThreadView = z.infer<typeof ThreadView>;
export const ThreadList = z.strictObject({ threads: z.array(ThreadView) });

export const CreateThreadBody = z.strictObject({
  connectorId: z.number().int().positive(),
  agent: AgentIdSchema,
  model: z.string().min(1).max(100).optional(),
  effort: z.string().min(1).max(20).optional(),
  page: ThreadPage,
});
export type CreateThreadBody = z.infer<typeof CreateThreadBody>;

export const PatchThreadBody = z
  .strictObject({
    title: z.string().trim().min(1).max(80).optional(),
    model: z.string().min(1).max(100).optional(),
    effort: z.string().min(1).max(20).nullable().optional(),
    archived: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, 'пустое изменение');
export type PatchThreadBody = z.infer<typeof PatchThreadBody>;

export const TimelineResponse = z.strictObject({
  seq: z.number().int(),
  rows: z.array(TimelineRow),
  removed: z.array(z.string()),
});
export type TimelineResponse = z.infer<typeof TimelineResponse>;
export const StepsResponse = z.strictObject({ rows: z.array(TimelineRow) });

export const MESSAGE_MAX = 8000;
export const MessageBody = z.strictObject({
  text: z.string().trim().min(1).max(MESSAGE_MAX),
  mode: z.enum(['turn', 'steer', 'queue']),
  key: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/u),
});
export type MessageBody = z.infer<typeof MessageBody>;
export const MessageResult = z.strictObject({
  state: z.enum(['sent', 'queued']),
  reason: z.enum(['turn_running', 'waiting_answer', 'offline', 'busy_computer', 'retry']).optional(),
});
export type MessageResult = z.infer<typeof MessageResult>;

export const ResolveBody = z.strictObject({
  nonce: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/u),
  decision: z.enum(['deny', 'allow_once', 'allow_thread', 'answer']),
  answers: z.array(z.string().max(500)).max(8).optional(),
});
export type ResolveBody = z.infer<typeof ResolveBody>;

export const FromDayBody = z.strictObject({
  date: z.iso.date(),
  personId: z.string().min(1).max(64),
  connectorId: z.number().int().positive().optional(),
  agent: AgentIdSchema.optional(),
});
export type FromDayBody = z.infer<typeof FromDayBody>;

/** WebSocket браузера `/api/agent/ws`: клиент → сервер. */
export const BrowserWsIn = z.strictObject({
  op: z.enum(['subscribe', 'unsubscribe']),
  threadId: z.string().min(1).max(64),
});
export type BrowserWsIn = z.infer<typeof BrowserWsIn>;

/** WebSocket браузера: сервер → клиент. */
export const BrowserWsOut = z.discriminatedUnion('op', [
  z.strictObject({ op: z.literal('changed'), threadId: z.string(), seq: z.number().int() }),
  z.strictObject({ op: z.literal('thread'), threadId: z.string(), status: ThreadStatus }),
  z.strictObject({ op: z.literal('connector'), connectorId: z.number().int(), online: z.boolean() }),
  z.strictObject({ op: z.literal('agents'), connectorId: z.number().int() }),
  z.strictObject({
    op: z.literal('install'),
    connectorId: z.number().int(),
    agent: AgentIdSchema,
    lines: z.array(z.string()),
  }),
  z.strictObject({ op: z.literal('subscribed'), threadId: z.string() }),
  z.strictObject({ op: z.literal('error'), code: z.string(), threadId: z.string().optional() }),
  z.strictObject({ op: z.literal('ping') }),
]);
export type BrowserWsOut = z.infer<typeof BrowserWsOut>;

/** Ответы адресов браузера (contracts/http-api.md): общие схемы для сервера и интерфейса. */
export const AgentCheckResponse = z.strictObject({ agent: AgentHealth });
export const InstallStarted = z.strictObject({ started: z.literal(true) });
export const DisableResponse = z.strictObject({ phase: z.literal('disabled') });
export const StopResponse = z.strictObject({ stopped: z.literal(true) });
export const ResolveResponse = z.strictObject({ state: z.literal('resolved') });
export const RetryQueued = z.strictObject({ state: z.literal('queued') });
export const ThreadResponse = z.strictObject({ thread: ThreadView });
export const QueueItem = z.strictObject({
  key: z.string(),
  text: z.string(),
  mode: z.enum(['turn', 'steer', 'queue']),
  reason: MessageResult.shape.reason.unwrap().nullable(),
  sendAt: z.string().nullable(),
  /** Повтор после лимита: ход, который повторяется, и номер попытки (до 5). */
  retryOf: z.string().nullable(),
  attempt: z.number().int().nullable(),
});
export type QueueItem = z.infer<typeof QueueItem>;
export const ThreadDetail = z.strictObject({ thread: ThreadView, queue: z.array(QueueItem) });
export type ThreadDetail = z.infer<typeof ThreadDetail>;
export const FromDayResponse = z.strictObject({
  threadId: z.string(),
  thread: ThreadView,
  draft: z.string(),
});
export type FromDayResponse = z.infer<typeof FromDayResponse>;

export const AGENT_WS_PING_MS = 25_000;
